import React from 'react';
import { Segment } from '@mairistem/composants';
import { toast } from 'react-toastify';
import { Sens, type Exercice } from '@mairistem/comptabilite';
import { Combobox, getErrorMessage } from '@mairistem/finances-utils';
import { isArray, isEmpty } from 'lodash';

import './FiltersTreeview.less';
import type SensOptions from '../../../../interfaces/pieces/bordereaux/SensOptions';
import { getExercices } from '../../../../services/entites/exercices';
import FiltresBordereauState from '../Bordereau/FiltresBordereauState';
import type BordereauStateType from '../../../../constants/liquidation/bordereaux/BordereauStateType';

interface FiltersTreeviewProps {
  onAnneeChange: (anneeObject: number) => void;
  onSensChange: (sens: SensOptions) => void;
  onFiltreBordereauChange: (bordereauStateType: number) => void;
  selectedAnnee: number;
  visibleStates: Set<BordereauStateType>;
}

const FILTRE_SENS_OPTIONS = [
  {
    key: 'T', text: 'Dépenses/Recettes', value: 'T', 'data-testid': 'filtreSensComboboxOptionTous',
  },
  {
    key: Sens.DEPENSE, text: 'Dépenses', value: Sens.DEPENSE, 'data-testid': 'filtreSensComboboxOptionDepense',
  },
  {
    key: Sens.RECETTE, text: 'Recettes', value: Sens.RECETTE, 'data-testid': 'filtreSensComboboxOptionRecette',
  },
];

const FiltersTreeview = ({
  onAnneeChange, onSensChange, onFiltreBordereauChange, selectedAnnee, visibleStates,
}: FiltersTreeviewProps) => {
  const [exercices, setExercices] = React.useState<Partial<Exercice>[]>([]);

  const fetchExercices = React.useCallback(async () => {
    try {
      const { data } = await getExercices(new URLSearchParams({ 'distincts[]': 'exe_annee', 'columns[]': 'exe_annee' }));
      const sortedData: Exercice[] = data.sort(
        (a: Partial<Exercice>, b: Partial<Exercice>) => b.anneeExercice - a.anneeExercice,
      );

      if (isArray(sortedData) && !isEmpty(sortedData)) {
        setExercices(sortedData);
        onAnneeChange(sortedData[0]?.anneeExercice);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur lors de la récupération des exercices'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAnneeChange = React.useCallback(
    (_, { value }: { value: string }) => {
      onAnneeChange(Number(value));
    },
    [onAnneeChange],
  );

  const handleSensChange = React.useCallback(
    (_, { value }: { value: SensOptions }) => {
      onSensChange(value);
    },
    [onSensChange],
  );

  React.useEffect(() => {
    fetchExercices();
  }, [fetchExercices]);

  return (
    <Segment>
      <div className="filtres-mouvements-container">
        <Combobox
          options={
            exercices?.map((exercice) => ({
              key: exercice.anneeExercice,
              text: `Exercice ${exercice.anneeExercice} - Tous les budgets`,
              value: exercice.anneeExercice,
              'data-testid': `filtreExerciceComboboxOption${exercice.anneeExercice}`,
            })) || []
          }
          placeholder="Exercice"
          value={selectedAnnee}
          onChange={handleAnneeChange}
          data-testid="filtreExerciceCombobox"
        />
        <Combobox
          options={FILTRE_SENS_OPTIONS}
          placeholder="Sens"
          onChange={handleSensChange}
          defaultValue="T"
          data-testid="filtreSensCombobox"
        />
        <FiltresBordereauState
          onChange={onFiltreBordereauChange}
          visibleStates={visibleStates}
        />
      </div>
    </Segment>
  );
};

export default FiltersTreeview;
